import React from "react";
import ReactDOM from "react-dom/client";
import * as Sentry from "@sentry/react";
import toast from "react-hot-toast";
import { RouterProvider } from "@tanstack/react-router";
import { QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { AuthProvider } from "./contexts/AuthContext";
import { DialogProvider } from "./contexts/DialogContext";
import { GoogleCalendarProvider } from "./contexts/GoogleCalendarContext";
import { ThemeProvider } from "./contexts/ThemeContext";
import { UIPreferencesProvider } from "./contexts/UIPreferencesContext";
import { router } from "./router";
import ErrorFallback from "./components/misc/ErrorFallback";
import { setupErrorInterceptor, captureNetworkError, isChromeEmptyRejected } from "./lib/errorInterceptor";
import NetworkErrorLog from "./components/misc/NetworkErrorLog";
import NotificationToaster from "./components/layout/NotificationToaster";
import { queryClient } from "./lib/queryClient";
import { PRERENDERED_LEGAL_ID } from "./lib/prerenderedLegalId";
import "./index.css";

setupErrorInterceptor();

Sentry.init({
  dsn: "https://b6fcd29640de2781dc7f39e75dee4a6b@o4510890871095296.ingest.de.sentry.io/4510890896719952",
  environment: import.meta.env.MODE,
  enabled: import.meta.env.PROD,
  integrations: [
    Sentry.browserTracingIntegration(),
    Sentry.replayIntegration(),
  ],
  tracesSampleRate: 0.1,
  replaysSessionSampleRate: 0,
  replaysOnErrorSampleRate: 1.0,
  // ResizeObserver fires this benign error when its callback triggers
  // another size change in the same frame; modern browsers handle it
  // gracefully but report it via window.onerror. It's not actionable
  // and pollutes the error stream — drop it before it reaches Sentry.
  ignoreErrors: [
    /ResizeObserver loop completed with undelivered notifications/,
    /ResizeObserver loop limit exceeded/,
  ],
  // Chrome rejects an internal promise with Error: Rejected (or the
  // string "Rejected") on public pages, with no `at` frames. ignoreErrors
  // cannot see the stack, and a non-Error rejection is stored as
  // "Non-Error promise rejection captured with value: Rejected".
  // Sentry issue JUDI-6.
  beforeSend(event, hint) {
    if (isChromeEmptyRejected(hint.originalException)) {
      return null;
    }
    return event;
  },
  // Sentry's SDK may capture a reference to window.fetch before our
  // interceptor patch runs (ES module evaluation order). Wrapping the
  // transport here guarantees that any send failure — e.g. from an
  // ad blocker or network error — is still recorded in NetworkErrorLog.
  transport: (options) => {
    const inner = Sentry.makeFetchTransport(options);
    return {
      send: async (envelope) => {
        try {
          return await inner.send(envelope);
        } catch (err) {
          const msg = err instanceof Error ? err.message : String(err);
          captureNetworkError(
            `Sentry transport error: ${msg}`,
            `# Sentry Transport Error\n\n**Error:** ${msg}${err instanceof Error && err.stack ? `\n\n## Stack\n\`\`\`\n${err.stack}\n\`\`\`` : ""}`,
          );
          throw err;
        }
      },
      flush: (timeout) => inner.flush(timeout),
    };
  },
});

// Network status monitoring
window.addEventListener("offline", () => {
  toast.error("You're offline. Changes may not be saved.", {
    id: "network-status",
    duration: Infinity,
  });
});
window.addEventListener("online", () => {
  toast.dismiss("network-status");
  toast.success("Back online", { id: "network-online", duration: 3000 });
});

document.getElementById(PRERENDERED_LEGAL_ID)?.remove();

ReactDOM.createRoot(document.getElementById("root")!).render(
  <React.StrictMode>
    <ThemeProvider>
      <QueryClientProvider client={queryClient}>
      <NotificationToaster />
      <AuthProvider>
        <DialogProvider>
          <UIPreferencesProvider>
            <NetworkErrorLog />
            <GoogleCalendarProvider>
              <Sentry.ErrorBoundary fallback={<ErrorFallback />}>
                <RouterProvider router={router} />
              </Sentry.ErrorBoundary>
            </GoogleCalendarProvider>
          </UIPreferencesProvider>
        </DialogProvider>
      </AuthProvider>
      {import.meta.env.DEV && <ReactQueryDevtools initialIsOpen={false} />}
      </QueryClientProvider>
    </ThemeProvider>
  </React.StrictMode>,
);
